import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { addPendingEntry } from "../lib/offline";
import { getCoords } from "../lib/geo";
import { deviceId } from "../lib/device";
import { useI18n, bilingual } from "../lib/i18n";
import { HeatBanner } from "../components/HeatBanner";
import BreakTimer from "../components/BreakTimer";
import { Play, Stop, Coffee, ForkKnife, Wrench, WarningOctagon, MapPin, ArrowsClockwise } from "@phosphor-icons/react";
import { uniqueRanches, repRanchId } from "../lib/ranchUtils";

function nowIso() { return new Date().toISOString(); }
function today() { return new Date().toISOString().slice(0, 10); }

export default function TimeCard() {
  const { t, lang } = useI18n();
  const [employees, setEmployees] = useState([]);
  const [crews, setCrews] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [ranches, setRanches] = useState([]);
  const [selectedRanch, setSelectedRanch] = useState("");
  const [selectedBlock, setSelectedBlock] = useState("");
  const [selectedCrew, setSelectedCrew] = useState("");
  const [mode, setMode] = useState("crew");
  const [selectedIds, setSelectedIds] = useState([]);
  const [empSearch, setEmpSearch] = useState("");
  const [jobCategory, setJobCategory] = useState("");
  const [jobCode, setJobCode] = useState("");
  const [activeEntries, setActiveEntries] = useState({});
  const [equipPickerFor, setEquipPickerFor] = useState(null);
  const [bulkEquipOpen, setBulkEquipOpen] = useState(false);
  const [bulkEquipIds, setBulkEquipIds] = useState([]);
  const [bulkNoEquipAck, setBulkNoEquipAck] = useState(false);
  const [operatingEquip, setOperatingEquip] = useState("");
  const [equipSearch, setEquipSearch] = useState("");
  const [breakTimerEmpId, setBreakTimerEmpId] = useState(null);
  const [crewBreak, setCrewBreak] = useState(null); // { kind, start } for whole-crew break timer
  const [confirmChange, setConfirmChange] = useState(null); // { emp } per-card, or { bulk:true }
  const [policy, setPolicy] = useState({ rest_minutes: 10, meal_minutes: 30, reminder: "" });

  const reload = async () => {
    const { data: te } = await api.get(`/time-entries?start_date=${today()}&end_date=${today()}`);
    const map = {};
    te.forEach((e) => { if (!e.clock_out) map[e.employee_id] = e; });
    setActiveEntries(map);
  };

  // Keep the full-screen break timer in sync with real data: if anyone is on an
  // open break (and no individual timer is showing), surface the crew timer; clear
  // it once every break has ended. This makes the timer resume after a page reload.
  useEffect(() => {
    const openBreaks = Object.values(activeEntries)
      .map((e) => (e.breaks || []).find((b) => !b.end))
      .filter(Boolean);
    if (openBreaks.length === 0) {
      setCrewBreak(null);
      return;
    }
    if (breakTimerEmpId) return; // an individual timer is already handling it
    const earliest = openBreaks.reduce((a, b) => (new Date(a.start) <= new Date(b.start) ? a : b));
    setCrewBreak((prev) =>
      prev && prev.start === earliest.start && prev.kind === earliest.kind
        ? prev
        : { kind: earliest.kind, start: earliest.start }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEntries, breakTimerEmpId]);

  const selectedJob = useMemo(() => jobs.find((j) => j.id === jobCode), [jobs, jobCode]);
  const reqEquip = !!selectedJob?.requires_equipment;
  const punchReady = !!selectedRanch && !!jobCategory && !!jobCode;
  const jobCategories = useMemo(() => {
    const map = new Map();
    jobs.forEach((j) => {
      const name = j.category || "Uncategorized";
      if (!map.has(name)) {
        map.set(name, { code: j.category_code || "", es: j.category_es || "" });
      } else if (!map.get(name).code && j.category_code) {
        map.set(name, { ...map.get(name), code: j.category_code });
      }
    });
    return [...map.entries()]
      .map(([name, v]) => ({ name, code: v.code, es: v.es }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [jobs]);
  const categoryJobs = useMemo(
    () => (jobCategory ? jobs.filter((j) => (j.category || "Uncategorized") === jobCategory) : []),
    [jobs, jobCategory]
  );

  const filteredEquip = useMemo(() => {
    if (!equipSearch) return equipment;
    const s = equipSearch.toLowerCase();
    return equipment.filter((eq) =>
      (eq.name || "").toLowerCase().includes(s) ||
      (eq.code || "").toLowerCase().includes(s) ||
      (eq.category || "").toLowerCase().includes(s)
    );
  }, [equipment, equipSearch]);

  const equipByCategory = useMemo(() => {
    const groups = {};
    filteredEquip.forEach((eq) => {
      const cat = eq.category || "Other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(eq);
    });
    return groups;
  }, [filteredEquip]);

  useEffect(() => {
    (async () => {
      try {
        const [emp, cr, j, eq, rn, te] = await Promise.all([
          api.get("/employees"),
          api.get("/crews"),
          api.get("/jobcodes"),
          api.get("/equipment"),
          api.get("/ranches"),
          api.get(`/time-entries?start_date=${today()}&end_date=${today()}`),
        ]);
        setEmployees(emp.data);
        setCrews(cr.data);
        setJobs(j.data);
        setEquipment(eq.data);
        setRanches(rn.data);
        try { const p = await api.get("/break-policy"); setPolicy(p.data); } catch { /* ignore */ }
        const map = {};
        te.data.forEach((e) => {
          if (!e.clock_out) map[e.employee_id] = e;
        });
        setActiveEntries(map);
      } catch { /* ignore */ }
    })();
  }, []);

  const crewMembers = selectedCrew
    ? employees.filter((e) => crews.find((c) => c.id === selectedCrew)?.member_ids.includes(e.id))
    : employees;
  const targetMembers = selectedIds.length ? crewMembers.filter((e) => selectedIds.includes(e.id)) : crewMembers;
  const crewAnyActive = targetMembers.some((e) => activeEntries[e.id]);
  const crewAnyMeal = targetMembers.some((e) => (activeEntries[e.id]?.breaks || []).some((b) => b.kind === "meal" && !b.end));
  const crewAnyRest = targetMembers.some((e) => (activeEntries[e.id]?.breaks || []).some((b) => b.kind === "rest" && !b.end));
  const toggleSelect = (id) => setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  useEffect(() => { setSelectedIds([]); }, [selectedCrew, mode]);

  const displayedMembers = useMemo(() => {
    if (mode === "individual") {
      const s = empSearch.trim().toLowerCase();
      return employees
        .filter((e) => e.active !== false)
        .filter((e) =>
          !s ||
          (e.name || "").toLowerCase().includes(s) ||
          (e.employee_code || "").toLowerCase().includes(s)
        );
    }
    return crewMembers;
  }, [mode, empSearch, employees, crewMembers]);

  const punchIn = async (emp) => {
    if (!punchReady) {
      alert("Select a ranch, category, and job code before clocking in.");
      return;
    }
    if (reqEquip && !bulkNoEquipAck && bulkEquipIds.length === 0) {
      setBulkEquipOpen(true);
      return;
    }
    const clientId = crypto.randomUUID();
    const coords = await getCoords();
    const payload = {
      client_id: clientId,
      employee_id: emp.id,
      crew_id: selectedCrew || null,
      ranch_id: (selectedBlock || selectedRanch) || null,
      block: (ranches.find((r) => r.id === selectedBlock)?.block) || null,
      job_code_id: jobCode || null,
      work_date: today(),
      clock_in: nowIso(),
      breaks: [],
      equipment_ids: bulkEquipIds,
      no_equipment_acknowledged: bulkNoEquipAck,
      latitude: coords?.lat ?? null,
      longitude: coords?.lng ?? null,
    };
    const optimistic = { ...payload, id: clientId, employee_name: emp.name, breaks: [], equipment_ids: bulkEquipIds };
    setActiveEntries((m) => ({ ...m, [emp.id]: optimistic }));
    if (navigator.onLine) {
      try {
        const { data } = await api.post("/time-entries", payload);
        setActiveEntries((m) => ({ ...m, [emp.id]: data }));
      } catch (e) {
        if (e.response?.status === 400) {
          setActiveEntries((m) => { const c = { ...m }; delete c[emp.id]; return c; });
          setBulkEquipOpen(true);
          return;
        }
        if (e.response?.status === 423) {
          setActiveEntries((m) => { const c = { ...m }; delete c[emp.id]; return c; });
          alert(e.response?.data?.detail || "REI active — clock-in blocked on this block.");
          return;
        }
        await addPendingEntry({ id: clientId, payload });
      }
    } else {
      await addPendingEntry({ id: clientId, payload });
    }
  };

  const bulkAction = async (action) => {
    const ids = targetMembers.map((e) => e.id);
    if (action === "punch_in") {
      if (!punchReady) {
        alert("Select a ranch, category, and job code before clocking in.");
        return;
      }
      if (reqEquip && !bulkNoEquipAck && bulkEquipIds.length === 0) {
        setBulkEquipOpen(true);
        return;
      }
    }
    try {
      const coords = action === "punch_in" ? await getCoords() : null;
      await api.post("/time-entries/bulk", {
        action,
        employee_ids: ids,
        crew_id: selectedCrew || null,
        ranch_id: (selectedBlock || selectedRanch) || null,
      block: (ranches.find((r) => r.id === selectedBlock)?.block) || null,
        job_code_id: jobCode || null,
        equipment_ids: bulkEquipIds,
        no_equipment_acknowledged: bulkNoEquipAck,
        latitude: coords?.lat ?? null,
        longitude: coords?.lng ?? null,
      });
      await reload();
      // Open / close the whole-crew full-screen break timer
      if (action === "meal_start") setCrewBreak({ kind: "meal", start: nowIso() });
      else if (action === "rest_start") setCrewBreak({ kind: "rest", start: nowIso() });
      else if (action === "meal_end" || action === "rest_end") setCrewBreak(null);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  // End / cancel the whole-crew break (from the full-screen timer)
  const endCrewBreak = async () => {
    if (!crewBreak) return;
    await bulkAction(crewBreak.kind === "meal" ? "meal_end" : "rest_end");
  };
  const cancelCrewBreak = async () => {
    if (!crewBreak) return;
    await bulkAction(crewBreak.kind === "meal" ? "meal_end" : "rest_end");
    setCrewBreak(null);
  };

  // Change jobs mid-shift: close the current segment and open a new one on the
  // selected ranch + job (split for accurate job costing). Carries equipment over.
  const changeJobFor = async (emp) => {
    const entry = activeEntries[emp.id];
    if (!entry) { alert("Employee must be clocked in to change jobs."); return; }
    if (!punchReady) { alert("Select a ranch, category, and the new job code at the top first."); return; }
    const ts = nowIso();
    const oldClientId = entry.client_id || entry.id;
    const closedBreaks = (entry.breaks || []).map((b) => (b.end ? b : { ...b, end: ts }));
    const outPayload = {
      client_id: oldClientId,
      employee_id: emp.id,
      crew_id: entry.crew_id || null,
      ranch_id: entry.ranch_id || null,
      job_code_id: entry.job_code_id || null,
      work_date: entry.work_date,
      clock_in: entry.clock_in,
      clock_out: ts,
      breaks: closedBreaks,
      equipment_ids: entry.equipment_ids || [],
    };
    const newClientId = crypto.randomUUID();
    const coords = await getCoords();
    const carried = entry.equipment_ids || [];
    const inPayload = {
      client_id: newClientId,
      employee_id: emp.id,
      crew_id: selectedCrew || entry.crew_id || null,
      ranch_id: (selectedBlock || selectedRanch) || null,
      block: (ranches.find((r) => r.id === selectedBlock)?.block) || null,
      job_code_id: jobCode || null,
      work_date: today(),
      clock_in: ts,
      breaks: [],
      equipment_ids: carried,
      no_equipment_acknowledged: carried.length === 0,
      latitude: coords?.lat ?? null,
      longitude: coords?.lng ?? null,
    };
    try {
      await api.post("/time-entries", outPayload);
      const { data } = await api.post("/time-entries", inPayload);
      setActiveEntries((m) => ({ ...m, [emp.id]: data }));
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  const changeJobBulk = async () => {
    if (!punchReady) { alert("Select a ranch, category, and the new job code at the top first."); return; }
    const targets = targetMembers.filter((e) => activeEntries[e.id]);
    if (targets.length === 0) { alert("No clocked-in crew members to change."); return; }
    for (const emp of targets) {
      // eslint-disable-next-line no-await-in-loop
      await changeJobFor(emp);
    }
    await reload();
  };

  const punchOut = async (emp) => {
    const entry = activeEntries[emp.id];
    if (!entry) return;
    const clientId = entry.client_id || entry.id;
    const payload = {
      client_id: clientId,
      employee_id: emp.id,
      crew_id: entry.crew_id || null,
      job_code_id: entry.job_code_id || null,
      work_date: entry.work_date,
      clock_in: entry.clock_in,
      clock_out: nowIso(),
      breaks: entry.breaks || [],
      equipment_ids: entry.equipment_ids || [],
    };
    setActiveEntries((m) => {
      const c = { ...m }; delete c[emp.id]; return c;
    });
    if (navigator.onLine) {
      try { await api.post("/time-entries", payload); }
      catch { await addPendingEntry({ id: clientId, payload }); }
    } else {
      await addPendingEntry({ id: clientId, payload });
    }
  };

  const saveBreaks = async (emp, entry, breaks) => {
    const next = { ...entry, breaks };
    setActiveEntries((m) => ({ ...m, [emp.id]: next }));
    const clientId = entry.client_id || entry.id;
    const payload = {
      client_id: clientId,
      employee_id: emp.id,
      crew_id: entry.crew_id || null,
      ranch_id: entry.ranch_id || null,
      job_code_id: entry.job_code_id || null,
      work_date: entry.work_date,
      clock_in: entry.clock_in,
      clock_out: null,
      breaks,
      equipment_ids: entry.equipment_ids || [],
    };
    if (navigator.onLine) {
      try { await api.post("/time-entries", payload); }
      catch { await addPendingEntry({ id: clientId, payload }); }
    } else {
      await addPendingEntry({ id: clientId, payload });
    }
  };

  const logBreakAudit = async (entry, breakType, event, extra) => {
    try {
      const coords = await getCoords();
      await api.post("/break-audit", {
        employee_id: entry.employee_id,
        crew_id: entry.crew_id || null,
        job_code: entry.job_code || null,
        break_type: breakType,
        event,
        latitude: coords?.lat ?? null,
        longitude: coords?.lng ?? null,
        device_id: deviceId(),
        device_info: navigator.userAgent,
        ...extra,
      });
    } catch { /* ignore */ }
  };

  // Start an individual break → opens the full-screen break timer for that worker
  const toggleBreak = async (emp, kind) => {
    const entry = activeEntries[emp.id];
    if (!entry) return;
    const open = (entry.breaks || []).find((b) => !b.end);
    if (open) {
      // A break is already running — just reopen its full-screen timer
      setBreakTimerEmpId(emp.id);
      return;
    }
    const breaks = [...(entry.breaks || []), { kind, start: nowIso() }];
    await saveBreaks(emp, entry, breaks);
    setBreakTimerEmpId(emp.id);
  };

  const endBreakFor = async (emp) => {
    const entry = activeEntries[emp.id];
    if (!entry) return;
    const ob = (entry.breaks || []).find((b) => !b.end);
    const end = nowIso();
    const breaks = (entry.breaks || []).map((b) => (!b.end ? { ...b, end } : b));
    await saveBreaks(emp, entry, breaks);
    if (ob) {
      const dur = Math.round(((new Date(end) - new Date(ob.start)) / 60000) * 100) / 100;
      logBreakAudit(entry, ob.kind, "completed", { start: ob.start, end, duration_minutes: dur });
    }
    setBreakTimerEmpId(null);
  };

  const cancelBreakFor = async (emp) => {
    const entry = activeEntries[emp.id];
    if (!entry) return;
    const ob = (entry.breaks || []).find((b) => !b.end);
    const breaks = (entry.breaks || []).filter((b) => b.end);
    await saveBreaks(emp, entry, breaks);
    if (ob) logBreakAudit(entry, ob.kind, "accidental", { start: ob.start });
    setBreakTimerEmpId(null);
  };

  const setEquip = async (emp, ids) => {
    const entry = activeEntries[emp.id];
    if (!entry) return;
    const next = { ...entry, equipment_ids: ids };
    setActiveEntries((m) => ({ ...m, [emp.id]: next }));
    const clientId = entry.client_id || entry.id;
    const payload = {
      client_id: clientId,
      employee_id: emp.id,
      crew_id: entry.crew_id || null,
      job_code_id: entry.job_code_id || null,
      work_date: entry.work_date,
      clock_in: entry.clock_in,
      clock_out: null,
      breaks: entry.breaks || [],
      equipment_ids: ids,
    };
    if (navigator.onLine) {
      try { await api.post("/time-entries", payload); }
      catch { await addPendingEntry({ id: clientId, payload }); }
    } else {
      await addPendingEntry({ id: clientId, payload });
    }
  };

  const timerEmp = breakTimerEmpId ? employees.find((e) => e.id === breakTimerEmpId) : null;
  const timerEntry = breakTimerEmpId ? activeEntries[breakTimerEmpId] : null;
  const timerBreak = timerEntry?.breaks?.find((b) => !b.end);

  return (
    <div className="space-y-6" data-testid="timecard-page">
      {timerEmp && timerBreak && (
        <BreakTimer
          kind={timerBreak.kind}
          start={timerBreak.start}
          durationMinutes={timerBreak.kind === "meal" ? policy.meal_minutes : policy.rest_minutes}
          reminder={policy.reminder}
          onEnd={() => endBreakFor(timerEmp)}
          onAccidental={() => cancelBreakFor(timerEmp)}
        />
      )}
      {!timerEmp && crewBreak && (
        <BreakTimer
          kind={crewBreak.kind}
          start={crewBreak.start}
          durationMinutes={crewBreak.kind === "meal" ? policy.meal_minutes : policy.rest_minutes}
          reminder={policy.reminder}
          onEnd={endCrewBreak}
          onAccidental={cancelCrewBreak}
        />
      )}

      {confirmChange && (() => {
        const r = ranches.find((x) => x.id === selectedRanch);
        const ranchLabel = r ? `${r.ranch_number} · ${r.name}` : "—";
        const jobLabel = selectedJob ? `${selectedJob.job_code || selectedJob.code} · ${bilingual(selectedJob.job || selectedJob.description, selectedJob.job_es, lang)}` : "—";
        const who = confirmChange.bulk
          ? `${targetMembers.filter((e) => activeEntries[e.id]).length} ${t("tc.members")}`
          : confirmChange.emp?.name;
        const run = async () => {
          const cc = confirmChange;
          setConfirmChange(null);
          if (cc.bulk) await changeJobBulk();
          else await changeJobFor(cc.emp);
        };
        return (
          <div className="fixed inset-0 bg-black/60 z-[55] flex items-center justify-center p-4" data-testid="change-job-confirm">
            <div className="bg-white border border-stone-200 shadow-2xl shadow-stone-900/20 w-full max-w-md p-6">
              <h3 className="text-2xl font-semibold tracking-tight mb-1" style={{ fontFamily: "'Fraunces', serif" }}>{t("tc.confirm_change_title")}</h3>
              <p className="text-sm text-stone-600 mb-4">{t("tc.confirm_change_body")}</p>
              <div className="border border-stone-200 divide-y divide-stone-200 text-sm mb-4">
                <div className="flex justify-between p-3"><span className="font-bold uppercase text-xs tracking-wide text-stone-500">{t("tc.confirm_who")}</span><span className="font-bold text-right">{who}</span></div>
                <div className="flex justify-between p-3 gap-3"><span className="font-bold uppercase text-xs tracking-wide text-stone-500">{t("tc.ranch")}</span><span className="font-medium text-right">{ranchLabel}</span></div>
                <div className="flex justify-between p-3 gap-3"><span className="font-bold uppercase text-xs tracking-wide text-stone-500">{t("tc.job_code")}</span><span className="font-medium text-right">{jobLabel}</span></div>
              </div>
              <div className="flex gap-3">
                <button data-testid="change-job-cancel" onClick={() => setConfirmChange(null)} className="flex-1 h-12 border border-stone-300 font-bold uppercase text-sm">{t("common.cancel")}</button>
                <button data-testid="change-job-confirm-btn" onClick={run} className="flex-1 h-12 bg-[#1F3D34] text-white font-bold uppercase text-sm border border-stone-200">{t("tc.confirm_change")}</button>
              </div>
            </div>
          </div>
        );
      })()}
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{t("tc.title")}</h1>
        <p className="text-sm font-medium tracking-wide uppercase text-stone-500">Real timestamps. No rounding. CA Labor Code compliant.</p>
      </div>

      <HeatBanner />

      <div className="inline-flex border border-stone-200 bg-white p-1" data-testid="timecard-mode-tabs">
        {[["crew", t("tc.tab_crew")], ["individual", t("tc.tab_individual")]].map(([m, label]) => (
          <button
            key={m}
            data-testid={`mode-tab-${m}`}
            onClick={() => setMode(m)}
            className={`h-11 px-5 font-bold uppercase text-xs tracking-wide transition-colors ${
              mode === m ? "bg-[#1F3D34] text-white" : "bg-transparent text-stone-600 hover:text-[#1F3D34]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          {mode === "crew" ? (
            <>
              <label className="text-sm font-bold uppercase tracking-[0.1em] mb-1 block">{t("tc.crew")}</label>
              <select
                data-testid="crew-select"
                value={selectedCrew}
                onChange={(e) => setSelectedCrew(e.target.value)}
                className="w-full h-14 px-4 bg-white border border-stone-200 text-lg font-medium"
              >
                <option value="">{t("tc.all_employees")}</option>
                {crews.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} · {c.member_ids.length} {t("tc.members")}</option>
                ))}
              </select>
            </>
          ) : (
            <>
              <label className="text-sm font-bold uppercase tracking-[0.1em] mb-1 block">{t("tc.tab_individual")}</label>
              <input
                data-testid="employee-search"
                value={empSearch}
                onChange={(e) => setEmpSearch(e.target.value)}
                placeholder={t("tc.search_employee")}
                className="w-full h-14 px-4 bg-white border border-stone-200 text-lg font-medium"
              />
            </>
          )}
        </div>
        <div>
          <label className="text-sm font-bold uppercase tracking-[0.1em] mb-1 block">{t("tc.ranch")} <span className="text-red-600">*</span></label>
          <select
            data-testid="ranch-select"
            value={repRanchId(ranches, selectedRanch)}
            onChange={(e) => { setSelectedRanch(e.target.value); setSelectedBlock(""); }}
            className="w-full h-14 px-4 bg-white border border-stone-200 text-lg font-medium"
          >
            <option value="">— Select Ranch —</option>
            {uniqueRanches(ranches).map((r) => (
              <option key={r.id} value={r.id}>
                {r.ranch_number} · {r.name}
              </option>
            ))}
          </select>
          {selectedRanch && (() => {
            const sel = ranches.find((r) => r.id === repRanchId(ranches, selectedRanch));
            const blocks = sel ? ranches.filter((r) => r.ranch_number === sel.ranch_number && r.block) : [];
            if (blocks.length === 0) return null;
            return (
              <select data-testid="block-select" value={selectedBlock} onChange={(e) => setSelectedBlock(e.target.value)}
                className="w-full h-12 px-4 mt-2 bg-white border border-stone-200 text-base font-medium">
                <option value="">Whole ranch — no specific block</option>
                {blocks.map((b) => (
                  <option key={b.id} value={b.id}>{b.block}{b.variety ? ` · ${b.variety}` : ""}{b.acres ? ` · ${b.acres} ac` : ""}</option>
                ))}
              </select>
            );
          })()}
          {selectedRanch && (() => {
            const sel = ranches.find((r) => r.id === repRanchId(ranches, selectedRanch));
            const blocks = sel ? ranches.filter((r) => r.ranch_number === sel.ranch_number && (r.block || r.variety)) : [];
            if (blocks.length === 0) return null;
            return (
              <details className="mt-2 bg-white border border-stone-200" data-testid="ranch-block-info">
                <summary className="px-3 py-2 text-xs font-bold uppercase text-[#1F3D34] cursor-pointer select-none">
                  Block info — {blocks.length} block{blocks.length === 1 ? "" : "s"}
                </summary>
                <div className="overflow-x-auto border-t border-stone-100">
                  <table className="w-full text-xs min-w-[480px]">
                    <thead><tr className="text-left text-[9px] uppercase text-stone-400 border-b border-stone-100">
                      <th className="px-3 py-1.5">Block</th><th className="px-3 py-1.5">Acres</th>
                      <th className="px-3 py-1.5">Variety</th><th className="px-3 py-1.5">Clone</th><th className="px-3 py-1.5">Rootstock</th>
                    </tr></thead>
                    <tbody>
                      {blocks.map((b) => (
                        <tr key={b.id} className="border-b border-stone-50">
                          <td className="px-3 py-1.5 font-bold">{b.block || "—"}</td>
                          <td className="px-3 py-1.5">{b.acres ?? "—"}</td>
                          <td className="px-3 py-1.5">{b.variety || "—"}</td>
                          <td className="px-3 py-1.5">{b.clone || "—"}</td>
                          <td className="px-3 py-1.5">{b.rootstock || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            );
          })()}
        </div>
        <div>
          <label className="text-sm font-bold uppercase tracking-[0.1em] mb-1 block">{t("tc.category")} <span className="text-red-600">*</span></label>
          <select
            data-testid="jobcategory-select"
            value={jobCategory}
            onChange={(e) => { setJobCategory(e.target.value); setJobCode(""); setBulkEquipIds([]); setBulkNoEquipAck(false); setOperatingEquip(""); }}
            className="w-full h-14 px-4 bg-white border border-stone-200 text-lg font-medium"
          >
            <option value="">{t("tc.select_category")}</option>
            {jobCategories.map((c) => (
              <option key={c.name} value={c.name}>{c.code ? `${c.code} · ` : ""}{bilingual(c.name, c.es, lang)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-bold uppercase tracking-[0.1em] mb-1 block">{t("tc.job_code")} <span className="text-red-600">*</span></label>
          <select
            data-testid="jobcode-select"
            value={jobCode}
            disabled={!jobCategory}
            onChange={(e) => { setJobCode(e.target.value); setBulkEquipIds([]); setBulkNoEquipAck(false); setOperatingEquip(""); }}
            className="w-full h-14 px-4 bg-white border border-stone-200 text-lg font-medium disabled:bg-stone-100 disabled:text-stone-400"
          >
            <option value="">{jobCategory ? t("tc.select_job") : t("tc.select_category_first")}</option>
            {categoryJobs.map((j) => (
              <option key={j.id} value={j.id}>{j.job_code || j.code} · {bilingual(j.job || j.description, j.job_es, lang)}{j.requires_equipment ? " ⚙" : ""}</option>
            ))}
          </select>
        </div>
      </div>

      {!punchReady && (
        <div className="bg-stone-100 border border-stone-300 px-4 py-3 text-sm font-medium text-stone-700 flex items-center gap-2" data-testid="punch-requirements-hint">
          <WarningOctagon weight="bold" size={20} />
          {t("tc.req_hint")}
        </div>
      )}

      {jobCode && (
        <div className="bg-white border border-stone-200 p-4 shadow-sm shadow-stone-900/5" data-testid="operating-equipment-prompt">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <div className="font-semibold tracking-tight text-sm">
                {t("tc.operating_q")}{reqEquip ? t("tc.required_for_job") : ""}
              </div>
              <div className="text-xs font-mono mt-1 text-stone-600" data-testid="operating-equipment-status">
                {bulkEquipIds.length > 0
                  ? `${bulkEquipIds.length} ${t("tc.equip_selected_suffix")}`
                  : bulkNoEquipAck
                    ? t("tc.no_equipment_status")
                    : t("tc.choose_option")}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="operating-equip-no"
                onClick={() => { setOperatingEquip("no"); setBulkNoEquipAck(true); setBulkEquipIds([]); }}
                className={`h-12 px-4 border font-bold uppercase text-xs ${bulkNoEquipAck ? "bg-[#1F3D34] text-white border-[#1F3D34]" : "bg-white border-stone-300"}`}
              >
                {t("tc.no_equipment")}
              </button>
              <button
                data-testid="operating-equip-yes"
                onClick={() => { setOperatingEquip("yes"); setBulkNoEquipAck(false); setBulkEquipOpen(true); }}
                className={`h-12 px-4 border font-bold uppercase text-xs flex items-center gap-1 ${(operatingEquip === "yes" || bulkEquipIds.length > 0) ? "bg-[#7C9673] text-white border-[#7C9673]" : "bg-white border-stone-300"}`}
              >
                <Wrench weight="bold" /> {t("tc.yes_choose")}
              </button>
            </div>
          </div>
        </div>
      )}

      {mode === "crew" && (
      <div className="bg-white border border-stone-200 p-3 shadow-sm shadow-stone-900/5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="text-xs font-medium tracking-wide uppercase text-stone-500">{t("tc.bulk_actions")}</div>
          <div className="flex items-center gap-2" data-testid="bulk-selection-bar">
            <span className="text-xs font-bold" data-testid="bulk-selection-count">
              {selectedIds.length
                ? `${selectedIds.length}/${crewMembers.length} ${t("tc.selected")}`
                : t("tc.whole_crew")}
            </span>
            {selectedIds.length > 0 && (
              <button data-testid="bulk-clear-selection" onClick={() => setSelectedIds([])}
                className="h-8 px-3 border border-stone-300 bg-white font-bold uppercase text-[10px]">{t("tc.clear_sel")}</button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <button
            data-testid={crewAnyActive ? "bulk-punch_out-btn" : "bulk-punch_in-btn"}
            onClick={() => bulkAction(crewAnyActive ? "punch_out" : "punch_in")}
            disabled={!crewAnyActive && !punchReady}
            className={`h-12 px-2 font-bold uppercase text-xs border border-stone-200 disabled:opacity-40 disabled:cursor-not-allowed ${crewAnyActive ? "bg-[#1F3D34] text-white" : "bg-[#7C9673] text-white"}`}
          >
            {crewAnyActive
              ? (selectedIds.length ? t("tc.bulk_punch_out_sel").replace("{n}", selectedIds.length) : t("tc.bulk_punch_out"))
              : (selectedIds.length ? t("tc.bulk_punch_in_sel").replace("{n}", selectedIds.length) : t("tc.bulk_punch_in"))}
          </button>
          <button
            data-testid={crewAnyMeal ? "bulk-meal_end-btn" : "bulk-meal_start-btn"}
            onClick={() => bulkAction(crewAnyMeal ? "meal_end" : "meal_start")}
            className={`h-12 px-2 font-bold uppercase text-xs border border-stone-200 ${crewAnyMeal ? "bg-[#1F3D34] text-white" : "bg-white"}`}
          >
            {crewAnyMeal
              ? t("tc.bulk_meal_end")
              : (lang === "es" ? `Iniciar Comida ${policy.meal_minutes} Min` : `Start ${policy.meal_minutes} Min Meal Time`)}
          </button>
          <button
            data-testid={crewAnyRest ? "bulk-rest_end-btn" : "bulk-rest_start-btn"}
            onClick={() => bulkAction(crewAnyRest ? "rest_end" : "rest_start")}
            className={`h-12 px-2 font-bold uppercase text-xs border border-stone-200 ${crewAnyRest ? "bg-[#1F3D34] text-white" : "bg-white"}`}
          >
            {crewAnyRest
              ? t("tc.bulk_rest_end")
              : (lang === "es" ? `Iniciar Descanso ${policy.rest_minutes} Min` : `Start ${policy.rest_minutes} Min Rest Time`)}
          </button>
          <button
            data-testid="bulk-change-job-btn"
            onClick={() => { if (punchReady) setConfirmChange({ bulk: true }); }}
            disabled={!punchReady}
            title={t("tc.change_job_hint")}
            className="h-12 px-2 font-bold uppercase text-xs border border-stone-200 bg-[#D8C7A1] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t("tc.bulk_change_job")}
          </button>
        </div>
        <div className="text-[11px] text-stone-500 mt-2">{t("tc.change_job_hint")}</div>
      </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {displayedMembers.length === 0 && (
          <div className="col-span-full text-center text-stone-500 font-medium tracking-wide uppercase text-xs py-8" data-testid="no-employees-match">
            {t("tc.no_match")}
          </div>
        )}
        {displayedMembers.map((emp) => {
          const active = activeEntries[emp.id];
          const onBreak = active?.breaks?.some((b) => !b.end);
          return (
            <div
              key={emp.id}
              data-testid={`crew-card-${emp.id}`}
              data-status={onBreak ? "break" : active ? "clocked-in" : "clocked-out"}
              className={`border p-4 shadow-sm shadow-stone-900/5 transition-colors ${
                onBreak
                  ? "bg-amber-100 border-amber-300"
                  : active
                  ? "bg-[#7C9673]/25 border-[#7C9673]"
                  : "bg-stone-100 border-stone-200"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                  {mode === "crew" && (
                    <input
                      type="checkbox"
                      data-testid={`select-emp-${emp.id}`}
                      checked={selectedIds.includes(emp.id)}
                      onChange={() => toggleSelect(emp.id)}
                      className="h-6 w-6 mt-1 accent-[#1F3D34] cursor-pointer shrink-0"
                    />
                  )}
                <div>
                  <div className="text-xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{emp.name}</div>
                  <div className="text-xs font-mono">{emp.employee_code} · ${emp.pay_rate}/hr</div>
                  {active && (
                    <div className="text-xs font-mono mt-1">
                      In: {new Date(active.clock_in).toLocaleTimeString()}
                    </div>
                  )}
                  {active && active.ranch_name && (
                    <div className="text-[10px] font-mono mt-0.5 text-stone-600 flex items-center gap-1" data-testid={`entry-ranch-${emp.id}`}>
                      <MapPin weight="fill" size={11} /> {active.ranch_name}
                    </div>
                  )}
                  {active && active.temp_f != null && (
                    <div className="text-[10px] font-mono mt-0.5 text-stone-500" data-testid={`entry-weather-${emp.id}`}>
                      {active.temp_f}°F{active.location_name ? ` · ${active.location_name}` : ""}{active.heat_alert ? " ⚠ HEAT" : ""}
                    </div>
                  )}
                </div>
                </div>
                {!active ? (
                  <button
                    data-testid={`punch-in-btn-${emp.id}`}
                    onClick={() => punchIn(emp)}
                    disabled={!punchReady}
                    className="h-14 px-6 bg-[#7C9673] text-white font-bold uppercase border border-stone-200 shadow-sm shadow-stone-900/5 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Play weight="fill" /> {t("tc.punch_in")}
                  </button>
                ) : (
                  <button
                    data-testid={`punch-out-btn-${emp.id}`}
                    onClick={() => punchOut(emp)}
                    className="h-14 px-6 bg-[#1F3D34] text-white font-bold uppercase border border-stone-200 shadow-sm shadow-stone-900/5   flex items-center gap-2"
                  >
                    <Stop weight="fill" /> {t("tc.punch_out")}
                  </button>
                )}
              </div>

              {active && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <button
                    data-testid={`meal-btn-${emp.id}`}
                    onClick={() => toggleBreak(emp, "meal")}
                    disabled={active.breaks?.some((b) => b.kind === "rest" && !b.end)}
                    className={`h-12 border border-stone-200 font-bold uppercase text-xs flex items-center justify-center gap-1 disabled:opacity-40 ${
                      active.breaks?.some((b) => b.kind === "meal" && !b.end) ? "bg-[#1F3D34] text-white" : "bg-white"
                    }`}
                  >
                    <ForkKnife weight="bold" /> Meal
                  </button>
                  <button
                    data-testid={`rest-btn-${emp.id}`}
                    onClick={() => toggleBreak(emp, "rest")}
                    disabled={active.breaks?.some((b) => b.kind === "meal" && !b.end)}
                    className={`h-12 border border-stone-200 font-bold uppercase text-xs flex items-center justify-center gap-1 disabled:opacity-40 ${
                      active.breaks?.some((b) => b.kind === "rest" && !b.end) ? "bg-[#1F3D34] text-white" : "bg-white"
                    }`}
                  >
                    <Coffee weight="bold" /> Rest
                  </button>
                  <button
                    data-testid={`equip-btn-${emp.id}`}
                    onClick={() => setEquipPickerFor(emp)}
                    className="h-12 border border-stone-200 font-bold uppercase text-xs flex items-center justify-center gap-1 bg-white"
                  >
                    <Wrench weight="bold" /> Equip ({active.equipment_ids?.length || 0})
                  </button>
                </div>
              )}
              {active && (
                <button
                  data-testid={`change-job-btn-${emp.id}`}
                  onClick={() => { if (punchReady) setConfirmChange({ emp }); }}
                  disabled={!punchReady}
                  title={t("tc.change_job_hint")}
                  className="mt-2 w-full h-12 border border-stone-200 bg-[#D8C7A1] font-bold uppercase text-xs flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ArrowsClockwise weight="bold" /> {t("tc.change_job")}
                </button>
              )}
              {active && active.breaks?.length > 0 && (
                <div className="mt-2 text-[10px] font-mono uppercase">
                  {active.breaks.map((b, i) => (
                    <div key={i}>
                      {b.kind}: {new Date(b.start).toLocaleTimeString()} {b.end ? `→ ${new Date(b.end).toLocaleTimeString()}` : "(open)"}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Equipment picker drawer */}
      {bulkEquipOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-4" data-testid="bulk-equipment-drawer">
          <div className="bg-white border border-stone-200 shadow-2xl shadow-stone-900/10 w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>
                {t("tc.equipment_for")} {selectedJob?.job_code || selectedJob?.code || "Crew"}
              </h3>
              <button onClick={() => setBulkEquipOpen(false)} className="h-10 px-3 border border-stone-200 font-bold uppercase text-xs">{t("tc.close")}</button>
            </div>
            <input
              data-testid="bulk-equipment-search"
              placeholder={`${t("common.search")} (name / # / category)`}
              value={equipSearch}
              onChange={(e) => setEquipSearch(e.target.value)}
              className="w-full h-12 px-3 border border-stone-200 mb-3"
            />
            <label className="flex items-center gap-2 mb-3 p-3 border border-stone-200 bg-[#D8C7A1] cursor-pointer">
              <input
                type="checkbox"
                data-testid="no-equipment-ack-checkbox"
                checked={bulkNoEquipAck}
                onChange={(e) => { setBulkNoEquipAck(e.target.checked); if (e.target.checked) setBulkEquipIds([]); }}
                className="w-5 h-5"
              />
              <span className="font-bold uppercase text-sm">{t("tc.no_equipment_ack")}</span>
            </label>
            <div className="space-y-3 max-h-[50vh] overflow-y-auto">
              {Object.entries(equipByCategory).map(([cat, list]) => (
                <div key={cat}>
                  <div className="text-xs font-medium tracking-wide uppercase mb-1 text-stone-500">{cat}</div>
                  <div className="space-y-1">
                    {list.map((eq) => {
                      const checked = bulkEquipIds.includes(eq.id);
                      return (
                        <label key={eq.id} className="flex items-center gap-3 p-2 border border-stone-200 cursor-pointer hover:bg-stone-50">
                          <input
                            data-testid={`bulk-equip-${eq.id}`}
                            type="checkbox"
                            checked={checked}
                            disabled={bulkNoEquipAck}
                            onChange={() => {
                              const next = checked ? bulkEquipIds.filter((x) => x !== eq.id) : [...bulkEquipIds, eq.id];
                              setBulkEquipIds(next);
                            }}
                            className="w-5 h-5"
                          />
                          <span className="font-bold">{eq.name}</span>
                          <span className="font-mono text-xs ml-auto">{eq.code}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <button
              data-testid="bulk-equip-confirm-btn"
              onClick={() => setBulkEquipOpen(false)}
              className="w-full mt-4 h-12 bg-[#1F3D34] text-white font-bold uppercase border border-stone-200 shadow-sm shadow-stone-900/5"
            >
              Confirm
            </button>
          </div>
        </div>
      )}

      {/* Per-employee equipment picker */}
      {equipPickerFor && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-4" data-testid="equipment-drawer">
          <div className="bg-white border border-stone-200 shadow-2xl shadow-stone-900/10 w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Equipment · {equipPickerFor.name}</h3>
              <button onClick={() => setEquipPickerFor(null)} className="h-10 px-3 border border-stone-200 font-bold uppercase text-xs">Close</button>
            </div>
            <div className="space-y-2">
              {equipment.map((eq) => {
                const checked = activeEntries[equipPickerFor.id]?.equipment_ids?.includes(eq.id);
                return (
                  <label key={eq.id} className="flex items-center gap-3 p-3 border border-stone-200 cursor-pointer hover:bg-stone-50">
                    <input
                      data-testid={`equip-${eq.id}`}
                      type="checkbox"
                      checked={!!checked}
                      onChange={() => {
                        const current = activeEntries[equipPickerFor.id]?.equipment_ids || [];
                        const next = checked ? current.filter((x) => x !== eq.id) : [...current, eq.id];
                        setEquip(equipPickerFor, next);
                      }}
                      className="w-5 h-5"
                    />
                    <span className="font-bold uppercase">{eq.name}</span>
                    <span className="font-mono text-xs ml-auto">{eq.code}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
